import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { useEffect } from 'react';
import { sqSummaryReportStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import {
  type ActionsFactory,
  type StoreInstance,
  createStore,
  createStoreInstance,
  useRegisterPersistedStore,
} from '@/stores';
import { CHARTS_ORDER } from '@/summaryReport/summaryData.constants';
import {
  I_CHART_KEY,
  MR_CHART_KEY,
  R_CHART_KEY,
  S_CHART_KEY,
  XBAR_CHART_KEY,
  primaryCharts,
  secondaryCharts,
} from '@/summaryReport/summaryData.types';
import {
  CONTROL_CHART_OUTPUTS,
  type ChartSlot,
  DEFAULT_STATE,
  INPUT_NAMES,
  InterpolationMethod,
  PRIMARY_CHART_SLOT,
  SECONDARY_CHART_SLOT,
  STATISTIC_OUTPUT_FEATURE_NAMES,
  type StatisticType,
  TIMESTAMP_DURATION,
  TIMESTAMP_END,
  TIMESTAMP_START,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import type {
  ChartKeys,
  PrimaryChartKeys,
  SecondaryChartKeys,
  StatisticsSummaryItem,
} from '@/summaryReport/summaryData.types';

import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolItem,
} from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

/** InputFeatureV1 extended with runtime fields not yet reflected in the generated SDK type */
interface InputFeatureRuntime extends InputFeatureV1 {
  inputText?: string | null;
  inputItem?: string | null;
  inputNumeric?: number | null;
  inputNumericUom?: string | null;
}

/**
 * Derives which control chart output groups and statistics were actually displayed
 * by cross-referencing instance outputs against what's currently on the trend.
 * Used during rehydrate to populate selections from existing trend state.
 */
function deriveSelectionsFromTrend(
  modelData: TemplateOutputV1 | undefined,
  instanceData: InstanceOutputV1 | undefined,
  charts: string[],
): {
  controlChartSelectedOutputs: Record<string, string[]>;
  selectedStatistics: StatisticType[];
} {
  const result = {
    controlChartSelectedOutputs: {} as Record<string, string[]>,
    selectedStatistics: [] as StatisticType[],
  };
  if (!modelData?.derivedFeatures || !instanceData?.outputs) {
    return result;
  }

  const trendItemIds = new Set<string>(getAllItems({}).map((item) => item.id));

  const displayedFeatureIds = new Set<string>();
  for (const output of instanceData.outputs) {
    if (output.featureId && output.outputId && trendItemIds.has(output.outputId)) {
      displayedFeatureIds.add(output.featureId);
    }
  }

  const featureIdToName: Record<string, string> = {};
  for (const feature of modelData.derivedFeatures) {
    if (feature.id && feature.name) {
      featureIdToName[feature.id] = feature.name;
    }
  }

  const displayedFeatureNames = new Set<string>();
  for (const featureId of displayedFeatureIds) {
    const name = featureIdToName[featureId];
    if (name) {
      displayedFeatureNames.add(name);
    }
  }

  const slotToChartKey: Partial<Record<ChartSlot, string>> = {};
  for (const chartKey of charts) {
    if (primaryCharts.includes(chartKey as PrimaryChartKeys)) {
      slotToChartKey[PRIMARY_CHART_SLOT] = chartKey;
    } else if (secondaryCharts.includes(chartKey as SecondaryChartKeys)) {
      slotToChartKey[SECONDARY_CHART_SLOT] = chartKey;
    }
  }

  for (const [slot, chartKey] of Object.entries(slotToChartKey) as [ChartSlot, string][]) {
    const slotOutputs = CONTROL_CHART_OUTPUTS[slot];
    const groups: string[] = [];
    for (const [groupKey, config] of Object.entries(slotOutputs)) {
      if ('featureName' in config) {
        if (displayedFeatureNames.has(config.featureName)) {
          groups.push(groupKey);
        }
      } else if ('featureNames' in config) {
        if (config.featureNames.some((name: string) => displayedFeatureNames.has(name))) {
          groups.push(groupKey);
        }
      }
    }
    if (groups.length > 0) {
      result.controlChartSelectedOutputs[chartKey] = groups;
    }
  }

  for (const [statType, featureName] of Object.entries(STATISTIC_OUTPUT_FEATURE_NAMES)) {
    if (displayedFeatureNames.has(featureName)) {
      result.selectedStatistics.push(statType as StatisticType);
    }
  }

  return result;
}

interface OperationalizeRehydratePayload extends ModelRehydratePayload {
  configParams?: ToolConfigParams;
  maxCapsuleDuration?: ValueWithUnitsItem;
  maxInterpolation?: ValueWithUnitsItem;
}

interface OperationalizeStatisticsSummaryState extends BaseToolPersistedState {
  id?: string;
  name: string;
  color: string;
  baseline: StatisticsSummaryItem | null;
  inputSignal: ToolItem | null;
  condition: ToolItem | null;
  alignmentCondition?: ToolItem;
  alignmentConditionFormula?: string;
  controlChartSelectedOutputs: Record<string, string[]>;
  originalParameters: ToolItem[];
  configParams: ToolConfigParams;
  periodicCondition?: ToolItem;
  selectedStatistics: StatisticType[];
  timestampPlacement: string;
  interpolationMethod: InterpolationMethod;
  maxCapsuleDuration: ValueWithUnitsItem;
  maxInterpolation?: ValueWithUnitsItem;
  rehydratedModelData?: TemplateOutputV1;
  rehydratedInstanceData?: InstanceOutputV1;
}

export interface OperationalizeStatisticsSummaryActions {
  setId: (id: string) => void;
  setName: (name: string) => void;
  setColor: (color: string) => void;
  setInputSignal: (signal: ToolItem | null) => void;
  setCondition: (condition: ToolItem | null) => void;
  setAlignmentCondition: (condition?: ToolItem) => void;
  setAlignmentConditionFormula: (formula?: string) => void;
  setBaseline: (baseline: StatisticsSummaryItem | null) => void;
  setControlChartSelectedOutputs: (controlChartSelectedOutputs: Record<string, string[]>) => void;
  setOriginalParameters: (parameters: ToolItem[]) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setConfigParams: (configParams: ToolConfigParams) => void;
  setPeriodicCondition: (item?: ToolItem) => void;
  setSelectedStatistics: (statistics: StatisticType[]) => void;
  setInterpolationMethod: (method: InterpolationMethod) => void;
  setTimestampPlacement: (placement: string) => void;
  setMaxCapsuleDuration: (duration: ValueWithUnitsItem) => void;
  setMaxInterpolation: (maxInterpolation?: ValueWithUnitsItem) => void;
  rehydrate: (payload: OperationalizeRehydratePayload) => void;
  reset: () => void;
}

export type LegacyOperationalizeStatisticsSummaryStore = StoreInstance<
  OperationalizeStatisticsSummaryState,
  OperationalizeStatisticsSummaryActions
> & {
  readonly persistenceLevel: 'WORKSHEET';
  dehydrate: () => OperationalizeStatisticsSummaryDehydratedState;
  rehydrate: (dehydratedState: Partial<OperationalizeStatisticsSummaryDehydratedState>) => void;
  advancedParametersCollapsed: boolean;
};

/**
 * Strips large chart data from baseline to avoid memory issues.
 * Preserves only essential fields: id, name, and spcCharts keys.
 */
function slimBaseline(item: StatisticsSummaryItem): StatisticsSummaryItem {
  const RULES_KEY = 'rules';
  const chartKeySet = new Set<ChartKeys>([I_CHART_KEY, MR_CHART_KEY, XBAR_CHART_KEY, R_CHART_KEY, S_CHART_KEY]);
  const isChartKey = (v: unknown): v is ChartKeys => {
    return typeof v === 'string' && chartKeySet.has(v as ChartKeys);
  };
  return {
    id: item.id,
    name: item.name,
    data: {
      spcCharts: item?.data?.spcCharts
        ? Object.keys(item.data.spcCharts).reduce(
            (acc, key) => {
              acc[key] = {};
              if (isChartKey(key)) {
                const rules = item?.data?.spcCharts?.[key]?.[RULES_KEY];
                if (rules) {
                  acc[key] = { rules };
                }
              }
              return acc;
            },
            {} as Record<string, unknown>,
          )
        : undefined,
    },
  } as StatisticsSummaryItem;
}

const initialState: OperationalizeStatisticsSummaryState = Object.freeze({
  id: undefined,
  color: '',
  name: '',
  baseline: null,
  controlChartSelectedOutputs: {},
  inputSignal: null,
  condition: null,
  alignmentCondition: undefined,
  alignmentConditionFormula: undefined,
  periodicCondition: undefined,
  selectedStatistics: DEFAULT_STATE.selectedStatistics,
  timestampPlacement: DEFAULT_STATE.timestampPlacement,
  interpolationMethod: DEFAULT_STATE.interpolationMethod,
  maxCapsuleDuration: DEFAULT_STATE.maxCapsuleDuration,
  maxInterpolation: undefined,
  originalParameters: [],
  advancedParametersCollapsed: true,
  configParams: { type: '', advancedParametersCollapsed: true },
  rehydratedModelData: undefined,
  rehydratedInstanceData: undefined,
});

export type OperationalizeStatisticsSummaryDehydratedState = Pick<
  OperationalizeStatisticsSummaryState,
  | 'id'
  | 'name'
  | 'controlChartSelectedOutputs'
  | 'condition'
  | 'alignmentCondition'
  | 'alignmentConditionFormula'
  | 'inputSignal'
  | 'baseline'
  | 'periodicCondition'
  | 'advancedParametersCollapsed'
  | 'selectedStatistics'
  | 'timestampPlacement'
  | 'interpolationMethod'
  | 'maxCapsuleDuration'
  | 'maxInterpolation'
>;

const operationalizeStatisticsSummaryActionsFactory: ActionsFactory<
  OperationalizeStatisticsSummaryState,
  OperationalizeStatisticsSummaryActions
> = (set, _get, resetState) => ({
  setId: (id: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.id = id;
    }),

  setName: (name: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.name = name;
    }),

  setColor: (color: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.color = color;
    }),

  setInputSignal: (signal: ToolItem | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.inputSignal = signal;
    }),

  setCondition: (condition: ToolItem | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.condition = condition;
    }),

  setAlignmentCondition: (condition?: ToolItem) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.alignmentCondition = condition;
    }),

  setAlignmentConditionFormula: (formula?: string) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.alignmentConditionFormula = formula;
    }),

  setBaseline: (baseline: StatisticsSummaryItem | null) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.baseline = baseline;
    }),

  setControlChartSelectedOutputs: (controlChartSelectedOutputs: Record<string, string[]>) =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.controlChartSelectedOutputs = controlChartSelectedOutputs;
    }),

  setPeriodicCondition: (item?: ToolItem) =>
    set((draft) => {
      draft.periodicCondition = item;
    }),

  setSelectedStatistics: (statistics: StatisticType[]) =>
    set((draft) => {
      draft.selectedStatistics = statistics;
    }),

  setTimestampPlacement: (placement: string) =>
    set((draft) => {
      draft.timestampPlacement = placement;
    }),

  setInterpolationMethod: (method: InterpolationMethod) =>
    set((draft) => {
      draft.interpolationMethod = method;
    }),

  setMaxCapsuleDuration: (duration: ValueWithUnitsItem) =>
    set((draft) => {
      draft.maxCapsuleDuration = duration;
    }),

  setMaxInterpolation: (maxInterpolation?: ValueWithUnitsItem) =>
    set((draft) => {
      draft.maxInterpolation = maxInterpolation;
    }),

  setOriginalParameters: (parameters: ToolItem[]) =>
    set((draft) => {
      draft.originalParameters = parameters;
    }),

  setAdvancedParametersCollapsed: (collapsed: boolean) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setConfigParams: (configParams: ToolConfigParams) =>
    set((draft) => {
      draft.configParams = configParams;
    }),

  rehydrate: (payload: OperationalizeRehydratePayload): void =>
    set((draft: OperationalizeStatisticsSummaryState) => {
      draft.id = payload.instanceId;
      draft.rehydratedModelData = payload.modelData;
      draft.rehydratedInstanceData = payload.instanceData;

      const inputFeatures = payload.modelData?.inputFeatures ?? [];
      const inputsByName = new Map(inputFeatures.map((f: InputFeatureRuntime) => [f.name ?? '', f]));

      const nameFeature = inputsByName.get(SeeqNames.Models.InstanceNameInputFeatureName);
      if (nameFeature) {
        // TODO: CRAB-53864: Handle duplicate names more gracefully
        draft.name = payload.instanceId ? String(nameFeature.inputText ?? '') : `${nameFeature.inputText ?? ''} (Copy)`;
      }

      const signalFeature = inputsByName.get(INPUT_NAMES.signalInput);
      if (signalFeature) {
        const itemId = signalFeature.inputItem ?? '';
        if (draft.inputSignal?.id !== itemId || !draft.inputSignal.signalMetadata) {
          const item = sqTrendSeriesStore.findItem(itemId) || { id: itemId };
          draft.inputSignal = {
            id: item.id,
            name: (item as ToolItem).name,
          } as ToolItem;
        }
      }

      const conditionFeature = inputsByName.get(INPUT_NAMES.restrictOutputToConditionInput);
      if (conditionFeature?.inputItem) {
        const item = sqTrendConditionStore.findItem(conditionFeature.inputItem) || { id: conditionFeature.inputItem };
        draft.condition = { id: item.id, name: (item as ToolItem).name } as ToolItem;
      }

      const alignmentConditionItemFeature = inputsByName.get(INPUT_NAMES.alignmentConditionInput);
      const alignmentConditionManualFeature = inputsByName.get(INPUT_NAMES.alignmentConditionManualInput);
      if (alignmentConditionItemFeature?.inputItem) {
        const item = sqTrendConditionStore.findItem(alignmentConditionItemFeature.inputItem) || {
          id: alignmentConditionItemFeature.inputItem,
        };
        draft.alignmentCondition = { id: item.id, name: (item as ToolItem).name } as ToolItem;
        draft.alignmentConditionFormula = undefined;
      } else if (alignmentConditionManualFeature?.inputText) {
        draft.alignmentCondition = undefined;
        draft.alignmentConditionFormula = alignmentConditionManualFeature.inputText;
      } else {
        draft.alignmentCondition = undefined;
        draft.alignmentConditionFormula = undefined;
      }

      const baselineFeature = inputsByName.get(INPUT_NAMES.baselineInput);
      if (baselineFeature?.inputItem) {
        const item = sqSummaryReportStore.findItem(baselineFeature.inputItem) || { id: baselineFeature.inputItem };
        draft.baseline = item as StatisticsSummaryItem;
      }

      const periodicFeature = inputsByName.get(INPUT_NAMES.periodicConditionInput);
      if (periodicFeature?.inputItem) {
        const item = sqTrendConditionStore.findItem(periodicFeature.inputItem) || { id: periodicFeature.inputItem };
        draft.periodicCondition = { id: item.id, name: (item as ToolItem).name } as ToolItem;
      }

      if (inputsByName.get(INPUT_NAMES.startKeyInput)?.inputItem) {
        draft.timestampPlacement = TIMESTAMP_START;
      } else if (inputsByName.get(INPUT_NAMES.endKeyInput)?.inputItem) {
        draft.timestampPlacement = TIMESTAMP_END;
      } else if (inputsByName.get(INPUT_NAMES.durationKeyInput)?.inputItem) {
        draft.timestampPlacement = TIMESTAMP_DURATION;
      }

      if (inputsByName.get(INPUT_NAMES.produceStats)?.inputItem) {
        if (inputsByName.get(INPUT_NAMES.toDiscreteInput)?.inputItem) {
          draft.interpolationMethod = InterpolationMethod.DISCRETE;
        } else if (inputsByName.get(INPUT_NAMES.toStepInput)?.inputItem) {
          draft.interpolationMethod = InterpolationMethod.STEP;
        } else {
          draft.interpolationMethod = InterpolationMethod.LINEAR;
        }
      }

      if (payload.configParams) {
        draft.configParams = payload.configParams;
      }
      if (payload.maxCapsuleDuration) {
        draft.maxCapsuleDuration = payload.maxCapsuleDuration;
      }
      if (payload.maxInterpolation) {
        draft.maxInterpolation = payload.maxInterpolation;
      }

      // Derive which charts were active from the model's input features (local variable, not stored)
      const chartKeys = draft.baseline?.data?.spcCharts
        ? (CHARTS_ORDER.filter((k) => k in (draft.baseline!.data!.spcCharts as Record<string, unknown>)) as ChartKeys[])
        : [];
      const charts: string[] = [];
      if (inputsByName.get(INPUT_NAMES.producePrimaryChart)?.inputItem) {
        charts.push(chartKeys[0] ?? I_CHART_KEY);
      }
      if (inputsByName.get(INPUT_NAMES.produceSecondaryChart)?.inputItem) {
        charts.push(chartKeys[1] ?? MR_CHART_KEY);
      }

      // Derive output group and statistic selections from what's currently on the trend.
      // For edit, workstep-level rehydration will subsequently override with persisted selections.
      const derived = deriveSelectionsFromTrend(payload.modelData, payload.instanceData, charts);
      draft.controlChartSelectedOutputs = derived.controlChartSelectedOutputs;
      draft.selectedStatistics = derived.selectedStatistics;
    }),

  reset: () => {
    resetState();
  },
});

// signalMetadata from the trend store can have a different structure than what
// fetchSignalWithMetadata returns (e.g. {uom: 'string', value: null} vs a proper
// maxInterpolation object). Persisting it causes isDiscreteSignal to misfire on
// rehydrate. Strip it so the organism always fetches fresh metadata on mount.
function stripSignalMetadata(signal: ToolItem | null): ToolItem | null {
  if (!signal) return null;
  return { ...signal, signalMetadata: undefined };
}

export const dehydrateOperationalizeStatisticsSummaryState = (
  state: OperationalizeStatisticsSummaryState,
): OperationalizeStatisticsSummaryDehydratedState => ({
  id: state.id,
  name: state.name,
  inputSignal: stripSignalMetadata(state.inputSignal),
  controlChartSelectedOutputs: state.controlChartSelectedOutputs,
  condition: state.condition,
  alignmentCondition: state.alignmentCondition,
  alignmentConditionFormula: state.alignmentConditionFormula,
  periodicCondition: state.periodicCondition,
  baseline: state.baseline ? slimBaseline(state.baseline) : null,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  selectedStatistics: state.selectedStatistics,
  timestampPlacement: state.timestampPlacement,
  interpolationMethod: state.interpolationMethod,
  maxCapsuleDuration: state.maxCapsuleDuration,
  maxInterpolation: state.maxInterpolation,
});

const STORE_NAME = 'OperationalizeStatisticsSummary';

const [
  OperationalizeStatsSummaryStoreProvider,
  useOperationalizeStatsSummaryStoreInstance,
  useOperationalizeStatsSummarySelector,
] = createStore(STORE_NAME, initialState, operationalizeStatisticsSummaryActionsFactory);

export { OperationalizeStatsSummaryStoreProvider };

export const useOperationalizeStatsSummaryId = () => useOperationalizeStatsSummarySelector((state) => state.id);
export const useOperationalizeStatsSummaryName = () => useOperationalizeStatsSummarySelector((state) => state.name);
export const useOperationalizeStatsSummaryColor = () => useOperationalizeStatsSummarySelector((state) => state.color);
export const useOperationalizeStatsSummaryBaseline = () =>
  useOperationalizeStatsSummarySelector((state) => state.baseline);
export const useOperationalizeStatsSummaryInputSignal = () =>
  useOperationalizeStatsSummarySelector((state) => state.inputSignal);
export const useOperationalizeStatsSummaryCondition = () =>
  useOperationalizeStatsSummarySelector((state) => state.condition);
export const useOperationalizeStatsSummaryAlignmentCondition = () =>
  useOperationalizeStatsSummarySelector((state) => state.alignmentCondition);
export const useOperationalizeStatsSummaryAlignmentConditionFormula = () =>
  useOperationalizeStatsSummarySelector((state) => state.alignmentConditionFormula);
export const useOperationalizeStatsSummaryOriginalParameters = () =>
  useOperationalizeStatsSummarySelector((state) => state.originalParameters);
export const useOperationalizeStatsSummaryPeriodicCondition = () =>
  useOperationalizeStatsSummarySelector((state) => state.periodicCondition);
export const useOperationalizeStatsSummaryAdvancedParametersCollapsed = () =>
  useOperationalizeStatsSummarySelector((state) => state.advancedParametersCollapsed);
export const useOperationalizeStatsSummaryConfigParams = () =>
  useOperationalizeStatsSummarySelector((state) => state.configParams);
export const useOperationalizeStatsSummarySelectedStatistics = () =>
  useOperationalizeStatsSummarySelector((state) => state.selectedStatistics);
export const useOperationalizeStatsSummaryTimestampPlacement = () =>
  useOperationalizeStatsSummarySelector((state) => state.timestampPlacement);
export const useOperationalizeStatsSummaryInterpolationMethod = () =>
  useOperationalizeStatsSummarySelector((state) => state.interpolationMethod);
export const useOperationalizeStatsSummaryMaxCapsuleDuration = () =>
  useOperationalizeStatsSummarySelector((state) => state.maxCapsuleDuration);
export const useOperationalizeStatsSummaryMaxInterpolation = () =>
  useOperationalizeStatsSummarySelector((state) => state.maxInterpolation);
export const useOperationalizeStatsSummaryControlChartSelectedOutputs = () =>
  useOperationalizeStatsSummarySelector((state) => state.controlChartSelectedOutputs);
export const useOperationalizeStatsSummaryRehydratedModelData = () =>
  useOperationalizeStatsSummarySelector((state) => state.rehydratedModelData);
export const useOperationalizeStatsSummaryRehydratedInstanceData = () =>
  useOperationalizeStatsSummarySelector((state) => state.rehydratedInstanceData);

export const useOperationalizeStatsSummaryActions = () => useOperationalizeStatsSummaryStoreInstance().actions;

export function legacyCreateOperationalizeStatisticsSummaryStore(): LegacyOperationalizeStatisticsSummaryStore {
  const store = createStoreInstance(initialState, operationalizeStatisticsSummaryActionsFactory, {
    storeName: STORE_NAME,
  });

  return Object.assign(store, {
    persistenceLevel: 'WORKSHEET' as const,
    dehydrate: () => dehydrateOperationalizeStatisticsSummaryState(store.getState()),
    rehydrate: (dehydratedState: Partial<OperationalizeStatisticsSummaryDehydratedState>) => {
      store.setState(dehydratedState, false);
    },
    advancedParametersCollapsed: store.getState().advancedParametersCollapsed,
  });
}

export const usePersistOperationalizeStatsSummaryStore = () => {
  const register = useRegisterPersistedStore();
  const store = useOperationalizeStatsSummaryStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqOperationalizeStatisticsSummaryStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateOperationalizeStatisticsSummaryState,
    });
  }, [register, store]);
};
